import { cn } from "@/ds/utils";
import type { LucideIcon } from "lucide-react";
import { createElement, type FC, type PropsWithChildren, type ReactNode } from "react";

export interface SectionRowProps extends PropsWithChildren {
	title: ReactNode;
	description?: ReactNode;
	icon?: LucideIcon;
	leading?: ReactNode;
	selected?: boolean;
	onClick?: () => void;
	className?: string;
}

export const SectionRow: FC<SectionRowProps> = props => {
	const {
		title,
		description,
		icon,
		leading,
		selected = false,
		onClick,
		className,
		children
	} = props;

	const content = (
		<>
			<div className="flex min-w-0 flex-1 items-center gap-3">
				{icon && createElement(icon, { className: "size-5 shrink-0 text-muted-foreground" })}
				{leading}

				<div className="min-w-0 flex-1">
					<div className="type-item">{title}</div>

					{
						description &&
						<div className="type-meta">{description}</div>
					}
				</div>
			</div>

			{
				children &&
				<div className="flex flex-col gap-3 @md:shrink-0 @md:flex-row @md:items-center">
					{children}
				</div>
			}
		</>
	);

	const rowClassName = cn(
		"flex w-full flex-col gap-3 border-b p-3 text-left @md:flex-row @md:items-center",
		selected && "bg-background-highlight",
		onClick && "cursor-pointer transition-colors duration-150 hover:bg-background-highlight focus-visible:bg-background-highlight outline-none",
		className
	);

	if (onClick) {
		return (
			<button type="button" className={rowClassName} onClick={onClick} aria-pressed={selected}>
				{content}
			</button>
		);
	}

	return (
		<div className={rowClassName}>
			{content}
		</div>
	);
};
